import React, { Component, ErrorInfo, ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AlertTriangle, Home, RotateCcw } from 'lucide-react'

interface Props {
  children: ReactNode
  // 讓外層用 pathname 當 key → location 一變就整顆 unmount + remount，error 狀態自動清掉
  // 不直接讀 useLocation 是為了讓 RouteErrorBoundary 本身保持 class component（必要才用 hook）
  resetKey?: string
}

interface State {
  error: Error | null
}

// 每條 route 的最後一道防線：lazy page 任何一個 runtime error 都不會把整個 React tree 弄壞，
// 從而導致使用者「按了第 6 頁後續全部卡住」。boundary 出現時提供「重試本頁 / 回到首頁」恢復路徑。
class RouteErrorBoundaryInner extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 留 console，方便 prod 用 DevTools 抓堆疊
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary] caught:', error, info.componentStack)
  }

  componentDidUpdate(prev: Props) {
    // 外層 key 變了會直接 remount，這裡只處理 resetKey 透過 prop 傳進來、但不希望 remount 的情境
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null })
    }
  }

  handleRetry = () => {
    // chunk 載入失敗時，單純清掉 error state 是死路：React.lazy 的物件是 module-level 的，
    // 一旦進入 Rejected 就永久黏住，重新 render 會立刻丟出同一個錯誤 → 又回到這張錯誤卡片，
    // 使用者只能自己按重新整理。這類錯誤改成整頁重載才真的能自救。
    const message = this.state.error?.message ?? ''
    if (/dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(message)) {
      window.location.reload()
      return
    }
    this.setState({ error: null })
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex items-center justify-center min-h-[50vh] px-4" role="alert">
        <div className="max-w-md w-full glass-card rounded-2xl p-6 text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-secondary-800">這個頁面出了點狀況</h2>
            <p className="text-sm text-secondary-600 mt-1 break-all">
              {this.state.error.message || '未知錯誤'}
            </p>
          </div>
          <div className="flex gap-2 justify-center pt-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm px-4 py-2"
            >
              <RotateCcw className="w-4 h-4" />
              重試本頁
            </button>
            <Link
              to="/timer"
              className="btn-primary inline-flex items-center gap-1.5 text-sm px-4 py-2"
            >
              <Home className="w-4 h-4" />
              回計時器
            </Link>
          </div>
        </div>
      </div>
    )
  }
}

// 公開的 wrapper：讀 location.pathname 當 key，讓切頁時整顆 boundary 重新掛載，
// 自動清掉錯誤狀態 → 故障頁面 click 走 sidebar 切到別頁就會恢復、不需要 reload
export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()
  return (
    <RouteErrorBoundaryInner key={location.pathname} resetKey={location.pathname}>
      {children}
    </RouteErrorBoundaryInner>
  )
}

export default RouteErrorBoundary
